'use client';

import { Button } from '@flipster/react/design-system';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import Earn from '../assets/img-earn.webp';
import ZeroSpread from '../assets/img-zerospread.webp';

const contents = [
  {
    title: 'Zero Spread,\nmaximum transparency',
    description: 'Unlock precise, cost-efficient trading with zero spread',
    image: ZeroSpread.src,
    imageWidthCss: css({ maxW: 590 }),
    width: 590,
    pointList: [
      'Accurate trades, ultra-thin slippage',
      'Transparent orders at true market price',
      'Lower costs across 23 pairs',
    ],
    learnMoreLink: '/zero-spread',
  },
  {
    title: 'Earn and trade\nsimultaneously',
    description: 'Trade actively and earn passively with the same funds in perpetual margin',
    image: Earn.src,
    imageWidthCss: css({ maxW: 528 }),
    width: 528,
    pointList: [
      'Funds remain fully liquid while you trade',
      'APR apply automatically while trading',
      'Earn up to 22% APR on supported assets',
    ],
    learnMoreLink: '/earn',
  },
];

const onClickLearnMore = (title: string, index: number) => {
  segment.track(MainEvent.IntroduceAdvantageLearnMoreClick, {
    section_title: title,
    list_order: index,
    event_location: 'main_page_view',
  });
};

const UniqueSettlingPoint = () => {
  return (
    <section className={css({ py: { base: 48, tablet: 64 } })}>
      {contents.map((content, index) => (
        <div
          key={content.title}
          className={flex({
            align: 'center',
            justify: 'space-between',
            w: 'auto',
            maxW: { tablet: 'desktopMaxWidth' },
            mx: { base: 16, smallDesktop: 'auto' },
            _odd: { flexDir: { base: 'column', tablet: 'row-reverse' } },
            _even: { mt: { base: 64, tablet: 96 }, flexDir: { base: 'column', tablet: 'row' } },
            gap: 20,
          })}
        >
          <div className={content.imageWidthCss}>
            <img
              src={content.image}
              alt={content.title}
              width={content.width}
              className={css({ rounded: { base: 8, tablet: 16 }, w: '100%', h: 'auto' })}
            />
          </div>
          <div
            className={css({ maxW: 590, pt: { base: 24, tablet: 0 }, textAlign: { base: 'center', tablet: 'left' } })}
          >
            <h2
              className={css({
                textStyle: 'Display/XL',
                color: 'content.default.level-4',
                whiteSpace: 'pre-line',
              })}
            >
              {content.title}
            </h2>
            <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-3', pt: 16 })}>
              {content.description}
            </p>
            <ul className={css({ pt: 26, display: { base: 'none', tablet: 'block' } })}>
              {content.pointList.map((point) => (
                <li key={point} className={flex({ align: 'center', gap: 8, py: 6 })}>
                  <CheckCircleIcon color={token('colors.content.accent.info')} size={24} />
                  <span className={css({ color: 'content.default.level-3', textStyle: 'Text/L/Regular' })}>
                    {point}
                  </span>
                </li>
              ))}
            </ul>
            <Button
              as='a'
              size='large'
              label='Learn more'
              css={{ minW: 120, mt: 48 }}
              href={content.learnMoreLink}
              onClick={() => onClickLearnMore(content.title, index)}
            />
          </div>
        </div>
      ))}
    </section>
  );
};

export default UniqueSettlingPoint;
